import React from 'react';
import styled from 'styled-components';
import { JsonEditor } from 'json-edit-react';
import { DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD } from '@services/rfp';
import { Body5, Container, H5, Separator } from '@ui';
import { CollapseButton, FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { Working } from '@ui/Working';
import { FormState } from '@ui/FormState';
import {
  createRfpTemplateFormField,
  deleteRfpTemplateFormField,
  fetchAvailableRfpTemplates,
  fetchRfpTemplateFormFields,
  updateRfpTemplateFormField,
} from '@services/rfp';
import {
  ReplaceTemplate,
  TEMPLATE_ACTIONS,
  TEMPLATE_ACTION_OPTIONS,
  TemplateDownloader,
  TemplateMetadataEditor,
} from './templateActions';
import { DocumentUploadStatusFlatDDQTemplateGeneration } from '../document/upload/DocumentUploadStatus';
import { suggestNextTemplateKey } from './suggestNextTemplateKey';
import { SlideFieldsEditor } from './SlideFieldsEditor';
import { isPowerPointTemplate } from './templateActions/templateBinaryUtils';

const TemplateSelectWrapper = styled.div`
  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }
`;

const FieldCard = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
`;

const FieldSummary = styled.div`
  cursor: pointer;
`;
const SaveButtonWrapper = styled.div`
  button {
    width: 420px;
  }
`;

const InputField = styled.input`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
`;

const TextareaField = styled.textarea`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  font-size: 14px;
  resize: vertical;
`;

const FieldInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const AddFieldActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

/** Lets `FlatButton fullWidth` resolve against a real width inside a flex row; cap matches FlatButton inner `max-width`. */
const AddFieldPrimaryAction = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: 420px;
`;

const DeleteButtonWrapper = styled.div``;

const AddFieldFormWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
`;

const JsonEditorWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
  overflow: auto;
`;

const FORM_FIELD_KEYS = [
  'template_key',
  'question',
  'clarification',
  'search_terms',
  'answer_format',
  'heading',
  'agent_name',
  'id',
  'sequence_number',
  'num_query_runs',
  'query_max_answer_tokens',
  'query_max_context_tokens',
  'sub_key_mode',
];
const PRIMARY_FORM_FIELD_KEY_SET = new Set(['template_key', 'question']);
const ADVANCED_FORM_FIELD_KEYS = FORM_FIELD_KEYS.filter((key) => !PRIMARY_FORM_FIELD_KEY_SET.has(key));
const TEXTAREA_FIELD_KEYS = new Set(['question', 'clarification', 'search_terms']);
/** Keys not duplicated in the compact header / special editors / server-only when listing "more" fields. */
const MORE_FIELDS_SECTION_EXCLUDED_KEYS = new Set([
  'template_key',
  'question',
  'sub_key_items',
  '_subKeyItemsError',
  'options',
]);

const getMoreSectionFieldKeys = (formField) => {
  const keys = Object.keys(formField || {}).filter((k) => !MORE_FIELDS_SECTION_EXCLUDED_KEYS.has(k));
  const knownOrdered = ADVANCED_FORM_FIELD_KEYS.filter((k) => keys.includes(k));
  const extra = keys.filter((k) => !knownOrdered.includes(k)).sort((a, b) => a.localeCompare(b));
  return [...knownOrdered, ...extra];
};
const EDIT_PHASE_ACTIONS = {
  EDIT_FIELDS: 'edit_fields',
};
const EDIT_PHASE_TEMPLATE_ACTION_OPTIONS = TEMPLATE_ACTION_OPTIONS.filter(
  (action) => action.id !== TEMPLATE_ACTIONS.EDIT_CELLS
);

const formatKeyName = (key) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getTemplateName = (template) => {
  return template?.name || template?.filename || `Template ${template?.id || ''}`.trim();
};

const getFieldSummary = (field) => {
  return field?.question || field?.template_key || `Field ${field?.id || ''}`.trim();
};

const isUserCreatedFormTemplate = (template) => {
  return Boolean(template?.template_type === 'form' && !template?.user_supplied_template_doc);
};

const getFieldEditorLabels = (template) => {
  if (isPowerPointTemplate(template)) {
    return {
      actionLabel: 'Edit Slide Fields',
      emptyMessage: 'No slide fields found for this template yet.',
      listLabel: 'Slide Fields',
      loadingMessage: 'Loading slide fields',
    };
  }

  return {
    actionLabel: 'Edit Form Fields',
    emptyMessage: 'No form fields found for this template yet.',
    listLabel: 'Form Fields',
    loadingMessage: 'Loading form fields',
  };
};

function ObjectFormFieldRow({ fieldKey, value, topSpacing, onChange }) {
  const [text, setText] = React.useState(() => JSON.stringify(value, null, 2));

  React.useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value, fieldKey]);

  const handleBlur = React.useCallback(() => {
    try {
      onChange(fieldKey, JSON.parse(text));
    } catch {
      setText(JSON.stringify(value, null, 2));
    }
  }, [fieldKey, onChange, text, value]);

  return (
    <Container top={topSpacing}>
      <FieldInputWrapper>
        <Body5 bold>{formatKeyName(fieldKey)}</Body5>
        <TextareaField value={text} onChange={(e) => setText(e.target.value)} onBlur={handleBlur} />
      </FieldInputWrapper>
    </Container>
  );
}

function FormFieldInputs({ formField, onChange, useCompactFieldForm = false, siblingTemplateKeys = [], subKeyEditor }) {
  const [showMoreFields, setShowMoreFields] = React.useState(false);

  const handleSuggestKey = React.useCallback(() => {
    onChange('template_key', suggestNextTemplateKey([...siblingTemplateKeys, formField.template_key ?? '']));
  }, [siblingTemplateKeys, formField.template_key, onChange]);

  const renderFieldRow = (key, topSpacing) => {
    const raw = formField[key];
    const displayValue = raw == null ? '' : typeof raw === 'boolean' ? (raw ? 'true' : 'false') : String(raw);
    return (
      <Container top={topSpacing} key={key}>
        <FieldInputWrapper>
          <Body5 bold>{formatKeyName(key)}</Body5>
          {TEXTAREA_FIELD_KEYS.has(key) ? (
            <TextareaField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          ) : (
            <InputField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          )}
        </FieldInputWrapper>
      </Container>
    );
  };

  const renderMoreSectionFieldRow = (key, topSpacing) => {
    const raw = formField[key];
    if (raw !== null && typeof raw === 'object') {
      return <ObjectFormFieldRow key={key} fieldKey={key} value={raw} topSpacing={topSpacing} onChange={onChange} />;
    }
    const displayValue = raw == null ? '' : typeof raw === 'boolean' ? (raw ? 'true' : 'false') : String(raw);
    return (
      <Container top={topSpacing} key={key}>
        <FieldInputWrapper>
          <Body5 bold>{formatKeyName(key)}</Body5>
          {TEXTAREA_FIELD_KEYS.has(key) ? (
            <TextareaField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          ) : (
            <InputField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          )}
        </FieldInputWrapper>
      </Container>
    );
  };

  if (!useCompactFieldForm) {
    return (
      <Container top={16}>
        {FORM_FIELD_KEYS.map((key, index) => renderFieldRow(key, index === 0 ? 0 : 16))}
        {subKeyEditor}
      </Container>
    );
  }

  return (
    <Container top={16}>
      <FieldInputWrapper>
        <Container row verticallyCenter spaceBetween fullWidth bottom={8}>
          <Body5 bold>{formatKeyName('template_key')}</Body5>
          <TransparentFlatButton onClick={handleSuggestKey}>Suggest key</TransparentFlatButton>
        </Container>
        <InputField value={formField.template_key || ''} onChange={(e) => onChange('template_key', e.target.value)} />
      </FieldInputWrapper>
      {renderFieldRow('question', 16)}
      <Container top={16} row verticallyCenter gap={8}>
        <Container bottom={8}>
          <FieldSummary
            role="button"
            tabIndex={0}
            onClick={() => setShowMoreFields((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowMoreFields((v) => !v);
              }
            }}
          >
            <Body5 bold uppercase>
              {showMoreFields ? 'Fewer Fields' : 'More Fields'}
            </Body5>
          </FieldSummary>
        </Container>

        <CollapseButton isCollapsed={!showMoreFields} setCollapsed={(collapsed) => setShowMoreFields(!collapsed)} />
      </Container>
      {showMoreFields && (
        <>
          {getMoreSectionFieldKeys(formField).map((key) => renderMoreSectionFieldRow(key, 16))}
          {subKeyEditor}
        </>
      )}
    </Container>
  );
}

function SubKeyItemsEditor({ formField, onInitializeSubKeyItems, onClearSubKeyItems, onSubKeyItemsChange }) {
  return (
    <Container top={16}>
      <FieldInputWrapper>
        <Body5 bold>Sub Key Items</Body5>
        {formField._subKeyItemsError && (
          <Container top={8}>
            <ErrorText>{formField._subKeyItemsError}</ErrorText>
          </Container>
        )}
        {formField.sub_key_items === null ? (
          <>
            <Container top={8}>
              <Body5>No JSON value set. This field will be saved as `null` until you initialize it.</Body5>
            </Container>
            <Container top={12}>
              <ActionsWrapper>
                <FlatButton onClick={onInitializeSubKeyItems}>Initialize JSON</FlatButton>
              </ActionsWrapper>
            </Container>
          </>
        ) : (
          <>
            <Container top={8}>
              <JsonEditorWrapper>
                <JsonEditor data={formField.sub_key_items} setData={onSubKeyItemsChange} />
              </JsonEditorWrapper>
            </Container>
            <Container top={12}>
              <ActionsWrapper>
                <TransparentFlatButton onClick={onClearSubKeyItems}>Clear JSON</TransparentFlatButton>
              </ActionsWrapper>
            </Container>
          </>
        )}
      </FieldInputWrapper>
    </Container>
  );
}

function TemplateFormFieldCard({
  templateId,
  field,
  onUpdate,
  onDelete,
  useCompactFieldForm = false,
  siblingTemplateKeys = [],
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [formField, setFormField] = React.useState(field);
  const [formError, setFormError] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    setFormField(field);
  }, [field]);

  const handleFieldChange = React.useCallback((key, value) => {
    setFormField((currentField) => ({
      ...currentField,
      [key]: value,
    }));
  }, []);

  const handleSubKeyItemsChange = React.useCallback((value) => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: value,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleInitializeSubKeyItems = React.useCallback(() => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: {},
      _subKeyItemsError: null,
    }));
  }, []);

  const handleClearSubKeyItems = React.useCallback(() => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: null,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleSave = async () => {
    setFormError(null);
    setMessage(null);
    setIsSaving(true);

    const { ok, data, error } = await updateRfpTemplateFormField(templateId, field.id, formField);

    setIsSaving(false);
    if (ok) {
      const updatedField = data?.id ? data : formField;
      onUpdate(updatedField);
      setMessage('Field saved successfully.');
      return;
    }

    setFormError(error || 'Something went wrong saving the field.');
  };

  const handleDelete = async () => {
    setFormError(null);
    setMessage(null);
    setIsDeleting(true);

    const { ok, error } = await deleteRfpTemplateFormField(templateId, field.id);

    setIsDeleting(false);
    if (ok) {
      onDelete(field.id);
      return;
    }

    setFormError(error || 'Something went wrong deleting the field.');
  };

  return (
    <FieldCard>
      <FieldSummary onClick={() => setIsCollapsed((currentValue) => !currentValue)}>
        <Body5 bold>{formField.template_key || `Field ${field.id}`}</Body5>
        <Container top={4}>
          <Body5>{getFieldSummary(formField)}</Body5>
        </Container>
      </FieldSummary>
      {!isCollapsed && (
        <>
          <FormFieldInputs
            formField={formField}
            onChange={handleFieldChange}
            useCompactFieldForm={useCompactFieldForm}
            siblingTemplateKeys={siblingTemplateKeys}
            subKeyEditor={
              <SubKeyItemsEditor
                formField={formField}
                onClearSubKeyItems={handleClearSubKeyItems}
                onInitializeSubKeyItems={handleInitializeSubKeyItems}
                onSubKeyItemsChange={handleSubKeyItemsChange}
              />
            }
          />
          <Container top={16}>
            {message && (
              <Container top={16}>
                <Body5 bold>{message}</Body5>
              </Container>
            )}
            {formError && (
              <Container top={16}>
                <ErrorText>{formError}</ErrorText>
              </Container>
            )}
            <Container top={16}>
              <ActionsWrapper>
                <SaveButtonWrapper>
                  <FlatButton
                    fullWidth
                    disabled={isSaving || isDeleting || Boolean(formField._subKeyItemsError)}
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </FlatButton>
                </SaveButtonWrapper>
                <DeleteButtonWrapper>
                  <TransparentFlatButton disabled={isSaving || isDeleting} onClick={handleDelete}>
                    {isDeleting ? 'Deleting...' : 'Delete Field'}
                  </TransparentFlatButton>
                </DeleteButtonWrapper>
              </ActionsWrapper>
            </Container>
          </Container>
        </>
      )}
    </FieldCard>
  );
}

function AddFormField({ templateId, onComplete, onCancel, useCompactFieldForm = false, siblingTemplateKeys = [] }) {
  const [formField, setFormField] = React.useState(DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD);
  const [formError, setFormError] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleFieldChange = React.useCallback((key, value) => {
    setFormField((currentField) => ({
      ...currentField,
      [key]: value,
    }));
  }, []);

  const handleSubKeyItemsChange = React.useCallback((value) => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: value,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleInitializeSubKeyItems = React.useCallback(() => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: {},
      _subKeyItemsError: null,
    }));
  }, []);

  const handleClearSubKeyItems = React.useCallback(() => {
    setFormField((currentField) => ({
      ...currentField,
      sub_key_items: null,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleSave = async () => {
    setFormError(null);
    setIsSaving(true);

    const { ok, data, error } = await createRfpTemplateFormField(templateId, formField);

    setIsSaving(false);
    if (ok) {
      onComplete(data);
      setFormField(DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD);
      return;
    }

    setFormError(error || 'Something went wrong creating the field.');
  };

  return (
    <Container>
      <H5>Add Field</H5>
      <FormFieldInputs
        formField={formField}
        onChange={handleFieldChange}
        useCompactFieldForm={useCompactFieldForm}
        siblingTemplateKeys={siblingTemplateKeys}
        subKeyEditor={
          <SubKeyItemsEditor
            formField={formField}
            onClearSubKeyItems={handleClearSubKeyItems}
            onInitializeSubKeyItems={handleInitializeSubKeyItems}
            onSubKeyItemsChange={handleSubKeyItemsChange}
          />
        }
      />
      {formError && (
        <Container top={16}>
          <ErrorText>{formError}</ErrorText>
        </Container>
      )}
      <Container top={16}>
        <AddFieldActionsWrapper>
          <AddFieldPrimaryAction>
            <FlatButton fullWidth disabled={isSaving || Boolean(formField._subKeyItemsError)} onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </FlatButton>
          </AddFieldPrimaryAction>
          <TransparentFlatButton disabled={isSaving} onClick={onCancel}>
            Cancel
          </TransparentFlatButton>
        </AddFieldActionsWrapper>
      </Container>
    </Container>
  );
}

export function EditPhase({ onGoToUpload, entryTemplate = null }) {
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [mode, setMode] = React.useState(null);
  const [formFields, setFormFields] = React.useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = React.useState(true);
  const [isRefreshingTemplates, setIsRefreshingTemplates] = React.useState(false);
  const [isLoadingFields, setIsLoadingFields] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [fieldsError, setFieldsError] = React.useState(null);
  const [showAddFieldForm, setShowAddFieldForm] = React.useState(false);
  const isFetchingTemplatesRef = React.useRef(false);
  const lastAutoSelectedEntryIdRef = React.useRef(null);

  React.useEffect(() => {
    if (!entryTemplate) {
      lastAutoSelectedEntryIdRef.current = null;
    }
  }, [entryTemplate]);

  const refreshTemplates = React.useCallback(
    async ({ isRefresh = false } = {}) => {
      if (isFetchingTemplatesRef.current) {
        return;
      }

      isFetchingTemplatesRef.current = true;
      setLoadError(null);

      if (isRefresh) {
        setIsRefreshingTemplates(true);
      } else {
        setIsLoadingTemplates(true);
      }

      const { ok, data, error } = await fetchAvailableRfpTemplates();

      if (ok) {
        let list = [...data];
        if (entryTemplate?.id != null && !list.some((template) => String(template.id) === String(entryTemplate.id))) {
          list = [{ id: entryTemplate.id, name: entryTemplate.name }, ...list];
        }
        setTemplates(list);
        if (entryTemplate?.id != null && lastAutoSelectedEntryIdRef.current !== String(entryTemplate.id)) {
          setSelectedTemplateId(String(entryTemplate.id));
          lastAutoSelectedEntryIdRef.current = String(entryTemplate.id);
        }
      } else {
        setLoadError(error || 'Something went wrong loading available templates.');
      }

      if (isRefresh) {
        setIsRefreshingTemplates(false);
      } else {
        setIsLoadingTemplates(false);
      }

      isFetchingTemplatesRef.current = false;
    },
    [entryTemplate]
  );

  const loadFormFields = React.useCallback(async (templateId) => {
    if (!templateId) {
      setFormFields([]);
      setFieldsError(null);
      return;
    }

    setIsLoadingFields(true);
    setFieldsError(null);
    setShowAddFieldForm(false);

    const { ok, data, error } = await fetchRfpTemplateFormFields(templateId);

    if (ok) {
      setFormFields(data);
    } else {
      setFormFields([]);
      setFieldsError(error || 'Something went wrong loading template fields.');
    }

    setIsLoadingFields(false);
  }, []);

  React.useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  React.useEffect(() => {
    if (!selectedTemplateId) {
      setMode(null);
      setFormFields([]);
      setFieldsError(null);
      setShowAddFieldForm(false);
      return;
    }

    const selectedTemplateStillExists = templates.some(
      (template) => String(template.id) === String(selectedTemplateId)
    );
    if (!selectedTemplateStillExists) {
      setSelectedTemplateId('');
      setMode(null);
      setFormFields([]);
      setShowAddFieldForm(false);
      return;
    }

    const selectedTemplate = templates.find((template) => String(template.id) === String(selectedTemplateId)) || null;
    if (isPowerPointTemplate(selectedTemplate)) {
      setFormFields([]);
      setFieldsError(null);
      setShowAddFieldForm(false);
      return;
    }

    loadFormFields(selectedTemplateId);
  }, [loadFormFields, selectedTemplateId, templates]);

  const selectedTemplate = React.useMemo(() => {
    return templates.find((template) => String(template.id) === String(selectedTemplateId)) || null;
  }, [selectedTemplateId, templates]);

  const useCompactFieldForm = isUserCreatedFormTemplate(selectedTemplate);
  const selectedTemplateIsPowerPoint = isPowerPointTemplate(selectedTemplate);
  const fieldEditorLabels = getFieldEditorLabels(selectedTemplate);

  const handleFieldCreateComplete = React.useCallback(
    async (field) => {
      setShowAddFieldForm(false);

      if (field?.id) {
        setFormFields((currentFields) => [...currentFields, field]);
        return;
      }

      await loadFormFields(selectedTemplateId);
    },
    [loadFormFields, selectedTemplateId]
  );

  const handleFieldUpdate = React.useCallback((updatedField) => {
    setFormFields((currentFields) =>
      currentFields.map((field) => (field.id === updatedField.id ? updatedField : field))
    );
  }, []);

  const handleFieldDelete = React.useCallback((fieldId) => {
    setFormFields((currentFields) => currentFields.filter((field) => field.id !== fieldId));
  }, []);

  const handleSelectedTemplateChange = React.useCallback((updatedTemplate) => {
    setTemplates((currentTemplates) =>
      currentTemplates.map((template) => (template.id === updatedTemplate.id ? updatedTemplate : template))
    );
  }, []);

  const handleActionCancel = React.useCallback(() => {
    setMode(null);
    setShowAddFieldForm(false);
  }, []);

  const handleFileUploadComplete = React.useCallback(() => {
    refreshTemplates({ isRefresh: true });
  }, [refreshTemplates]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    setMode(null);
    setShowAddFieldForm(false);
  };

  if (isLoadingTemplates) {
    return (
      <Container top={24}>
        <Working message="Loading available templates" />
      </Container>
    );
  }

  if (loadError && templates.length === 0) {
    return (
      <Container top={24}>
        <Body5>{loadError}</Body5>
        <Container top={24}>
          <FlatButton onClick={onGoToUpload}>Create Template From Document or Deck</FlatButton>
        </Container>
        <Container top={24}>
          <DocumentUploadStatusFlatDDQTemplateGeneration onFileUploadComplete={handleFileUploadComplete} />
        </Container>
      </Container>
    );
  }

  if (templates.length === 0) {
    return (
      <Container top={24}>
        <Body5>No uploaded templates available. Upload a Word document or PowerPoint deck first.</Body5>
        <Container top={16} centerAll>
          <FormState error={loadError} />
        </Container>
        <Container top={24}>
          <FlatButton onClick={onGoToUpload}>Create Template From Document or Deck</FlatButton>
        </Container>
        <Container top={24}>
          <DocumentUploadStatusFlatDDQTemplateGeneration onFileUploadComplete={handleFileUploadComplete} />
        </Container>
      </Container>
    );
  }

  return (
    <Container top={24}>
      <Container bottom={24}>
        <Container>
          <Body5 bold>Select Template</Body5>
        </Container>
        <Container top={8} centerAll>
          <FormState error={loadError} />
        </Container>
        <Container top={8}>
          <TemplateSelectWrapper>
            {isRefreshingTemplates ? (
              <Working message="Refreshing available templates" />
            ) : (
              <select value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)}>
                <option value="">Select a template...</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {getTemplateName(template)}
                  </option>
                ))}
              </select>
            )}
          </TemplateSelectWrapper>
        </Container>
      </Container>

      <Container top={16} bottom={16}>
        <Separator />
      </Container>

      {selectedTemplate && (
        <Container top={16}>
          <Body5 bold>Template</Body5>
          <Container top={8} bottom={24}>
            <Body5>{getTemplateName(selectedTemplate)}</Body5>
          </Container>
          {mode === null && (
            <Container top={16}>
              <Body5 bold>Please select an action</Body5>
              {EDIT_PHASE_TEMPLATE_ACTION_OPTIONS.map((action, index) => (
                <Container top={index === 0 ? 24 : 16} key={action.id}>
                  <FlatButton fullWidth onClick={() => setMode(action.id)}>
                    {action.label}
                  </FlatButton>
                </Container>
              ))}
              <Container top={16}>
                <FlatButton fullWidth onClick={() => setMode(EDIT_PHASE_ACTIONS.EDIT_FIELDS)}>
                  {fieldEditorLabels.actionLabel}
                </FlatButton>
              </Container>
            </Container>
          )}

          {mode === 'edit_template' && (
            <Container top={16}>
              <TemplateMetadataEditor
                template={selectedTemplate}
                onTemplateChange={handleSelectedTemplateChange}
                onCancel={handleActionCancel}
              />
            </Container>
          )}

          {mode === 'download_template' && (
            <Container top={16}>
              <TemplateDownloader template={selectedTemplate} onCancel={handleActionCancel} />
            </Container>
          )}

          {mode === 'replace_template' && (
            <Container top={16}>
              <ReplaceTemplate
                template={selectedTemplate}
                onCancel={handleActionCancel}
                onUploadComplete={() => refreshTemplates({ isRefresh: true })}
              />
            </Container>
          )}

          {mode === EDIT_PHASE_ACTIONS.EDIT_FIELDS &&
            (selectedTemplateIsPowerPoint ? (
              <SlideFieldsEditor template={selectedTemplate} onCancel={handleActionCancel} />
            ) : isLoadingFields ? (
              <Container top={24}>
                <Working message={fieldEditorLabels.loadingMessage} />
              </Container>
            ) : (
              <>
                <Container top={24} row spaceBetween verticallyCenter fullWidth>
                  <Body5 bold>{fieldEditorLabels.listLabel}</Body5>
                  {!showAddFieldForm && (
                    <FlatButton fullWidth onClick={() => setShowAddFieldForm(true)}>
                      Add Field
                    </FlatButton>
                  )}
                </Container>

                {fieldsError && (
                  <Container top={16}>
                    <ErrorText>{fieldsError}</ErrorText>
                  </Container>
                )}

                {showAddFieldForm && (
                  <Container top={16}>
                    <AddFieldFormWrapper>
                      <AddFormField
                        templateId={selectedTemplateId}
                        onComplete={handleFieldCreateComplete}
                        onCancel={() => setShowAddFieldForm(false)}
                        useCompactFieldForm={useCompactFieldForm}
                        siblingTemplateKeys={formFields.map((f) => f.template_key)}
                      />
                    </AddFieldFormWrapper>
                  </Container>
                )}

                <Container top={16}>
                  <FlatButton fullWidth={false} onClick={handleActionCancel}>
                    Back To Actions
                  </FlatButton>
                </Container>

                {formFields.length === 0 && !fieldsError ? (
                  <Container top={16}>
                    <Body5>{fieldEditorLabels.emptyMessage}</Body5>
                  </Container>
                ) : (
                  <Container top={16}>
                    {formFields.map((field, index) => (
                      <Container top={index === 0 ? 0 : 16} key={field.id}>
                        <TemplateFormFieldCard
                          field={field}
                          onDelete={handleFieldDelete}
                          onUpdate={handleFieldUpdate}
                          templateId={selectedTemplateId}
                          useCompactFieldForm={useCompactFieldForm}
                          siblingTemplateKeys={formFields.filter((f) => f.id !== field.id).map((f) => f.template_key)}
                        />
                      </Container>
                    ))}
                  </Container>
                )}
              </>
            ))}
        </Container>
      )}

      <Container top={24}>
        <DocumentUploadStatusFlatDDQTemplateGeneration hideIfNoDocs onFileUploadComplete={handleFileUploadComplete} />
      </Container>
    </Container>
  );
}
